import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const presets = [
  { id: "today", label: "Today" }, { id: "week", label: "Last 7 days" },
  { id: "month30", label: "Last 30 days" }, { id: "month", label: "This month" },
];
export function dateRange(preset) {
  const end = new Date(), start = new Date(end);
  if (preset === "month") start.setUTCDate(1);
  else start.setUTCDate(start.getUTCDate() - (preset === "week" ? 6 : preset === "month30" ? 29 : 0));
  return { start_date: start.toISOString().slice(0, 10), end_date: end.toISOString().slice(0, 10) };
}
export const orderTypes = [
  { label: "All orders", value: "all" }, { label: "Online orders", value: "online" }, { label: "COD orders", value: "cod" },
];
export const displayDate = (date) => new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(date + "T00:00:00Z"));
export function money(amounts = {}) {
  const entries = Object.entries(amounts);
  if (!entries.length) return "—";
  return entries.map(([currency, amount]) => {
    try { return new Intl.NumberFormat("en", { style: "currency", currency, maximumFractionDigits: 4 }).format(Number(amount)); }
    catch { return amount + " " + currency; }
  }).join(" · ");
}
export const performanceSearch = (filters) => "?" + new URLSearchParams({
  start_date: filters.start_date, end_date: filters.end_date, order_type: filters.order_type,
}).toString();
export function usePerformanceFilters() {
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  const applied = useMemo(() => {
    const search = new URLSearchParams(query), defaults = dateRange("month30");
    return {
      start_date: search.get("start_date") || defaults.start_date,
      end_date: search.get("end_date") || defaults.end_date,
      order_type: search.get("order_type") || "all",
    };
  }, [query]);
  return [applied, (next) => setParams(new URLSearchParams(performanceSearch(next).slice(1)))];
}

export default function PerformanceFilters({ applied, onApply, reportFilters, loading }) {
  const [draft, setDraft] = useState(applied), [error, setError] = useState("");
  useEffect(() => { setDraft(applied); setError(""); }, [applied]);
  const activePreset = presets.find(({ id }) => {
    const range = dateRange(id);
    return range.start_date === draft.start_date && range.end_date === draft.end_date;
  })?.id;
  const changed = Object.keys(applied).some((key) => draft[key] !== applied[key]);
  const update = (next) => { setDraft((current) => ({ ...current, ...next })); setError(""); };
  const apply = (event) => {
    event.preventDefault();
    if (!draft.start_date || !draft.end_date) return setError("Choose both a start date and an end date.");
    if (draft.start_date > draft.end_date) return setError("Start date must be on or before end date.");
    if ((Date.parse(draft.end_date) - Date.parse(draft.start_date)) / 86400000 > 366) return setError("Choose a date range of 367 days or less.");
    setError(""); onApply({ ...draft });
  };
  return <form className="sa-performance-filters" onSubmit={apply} noValidate aria-label="Performance filters">
    <div className="sa-performance-filter-heading">
      <div className="sa-performance-range-heading"><h2>Date range</h2><span className="sa-timezone-badge">UTC</span></div>
      <div className="sa-performance-presets" role="group" aria-label="Quick date ranges">
        {presets.map(({ id, label }) => <button key={id} type="button" aria-pressed={activePreset === id} onClick={() => update(dateRange(id))}>{label}</button>)}
      </div>
    </div>
    <div className="sa-performance-filter-fields">
      <div className="sa-performance-date-range">
        <label htmlFor="performance-start">Start date<input id="performance-start" type="date" value={draft.start_date} onChange={(e) => update({ start_date: e.target.value })} aria-describedby={error ? "performance-date-error" : undefined} aria-invalid={Boolean(error)} /></label>
        <span className="sa-performance-date-separator" aria-hidden="true">to</span>
        <label htmlFor="performance-end">End date<input id="performance-end" type="date" value={draft.end_date} onChange={(e) => update({ end_date: e.target.value })} aria-describedby={error ? "performance-date-error" : undefined} aria-invalid={Boolean(error)} /></label>
      </div>
      <label htmlFor="performance-type">Order type<select id="performance-type" value={draft.order_type} onChange={(e) => update({ order_type: e.target.value })}>{orderTypes.map(({ label, value }) => <option key={value} value={value}>{label}</option>)}</select></label>
      <button className="sa-button sa-button--primary sa-performance-apply" type="submit" disabled={loading}>{loading ? "Loading..." : "Apply"}</button>
    </div>
    {error && <p className="sa-performance-filter-error" id="performance-date-error" role="alert">{error}</p>}
    <div className="sa-performance-filter-status" role="status">
      <span>{reportFilters ? "Showing " + displayDate(reportFilters.start_date) + " - " + displayDate(reportFilters.end_date) + " (UTC) / " + (orderTypes.find(({ value }) => value === reportFilters.order_type)?.label || "All orders") : "Select a date range to view your report."}</span>
      <span>{changed ? "Changes not applied" : loading ? "Updating report..." : "Dates include the full day"}</span>
    </div>
  </form>;
}
